import React, { useState, useEffect } from 'react';
import { BuildingOffice2Icon, UserIcon, CheckCircleIcon, XCircleIcon, InformationCircleIcon, DocumentTextIcon, EyeIcon, PencilSquareIcon, TrashIcon, EnvelopeIcon, PhoneIcon, DocumentPlusIcon, CurrencyDollarIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { SimpleOrganization } from "pages/Customers";
import { useLanguage } from "../../contexts/LanguageContext";
import { Customer } from "../../types/customer";
import { Tooltip } from "../common/Tooltip";
import { customerToKeyValuePairs } from '../../utils/customerDataMapper';
import PricingManagementModal from './PricingManagementModal';
import PodListModal from './PodListModal';
import toast from 'react-hot-toast';

interface CustomerListProps {
  customers: Customer[];
  organizations: SimpleOrganization[];
  onStatusChange: (customer: Customer) => void;
  onViewClick: (customer: Customer) => void;
  onEditClick: (customer: Customer) => void;
  onDeleteClick: (customer: Customer) => void;
  onDocumentClick: (customer: Customer) => void;
  onGenerateDocument: (customer: Customer) => void;
  onView: (customer: Customer) => void;
  onEdit: (customer: Customer) => void;
  onDelete: (customer: Customer) => void;
  hideActions?: boolean;
  selectedCustomers: Customer[];
  onSelectCustomer: (customer: Customer) => void;
  onSelectAll: () => void;
  isInSelectMode: boolean;
  showAdvancedSearch?: boolean;
  searchTerm?: string;
  onAdvancedSearchToggle: () => void;
  onRefresh: () => void;
  onConsultantClick?: (consultantId: string) => void;
}

const CustomerList: React.FC<CustomerListProps> = ({
  customers,
  organizations,
  onStatusChange,
  onViewClick,
  onEditClick,
  onDeleteClick,
  onDocumentClick,
  onGenerateDocument,
  onView,
  onEdit: onEditOriginal,
  onDelete: onDeleteOriginal,
  hideActions = false,
  selectedCustomers,
  onSelectCustomer,
  onSelectAll,
  onConsultantClick,
  isInSelectMode,
  showAdvancedSearch = false,
  searchTerm = '',
  onAdvancedSearchToggle,
  onRefresh
}) => {
  const { t } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [selectedCustomerForPricing, setSelectedCustomerForPricing] = useState<Customer | null>(null);
  const [isPodListModalOpen, setIsPodListModalOpen] = useState(false);
  const [selectedCustomerForPods, setSelectedCustomerForPods] = useState<Customer | null>(null);

  console.log('📋 CustomerList: Received props:', {
    customersCount: customers?.length || 0,
    organizationsCount: organizations?.length || 0,
    searchTerm,
    showAdvancedSearch
  });

  // Check for mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handlePricingManagement = (customer: Customer) => {
    setSelectedCustomerForPricing(customer);
    setIsPricingModalOpen(true);
  };

  const handlePricingUpdate = async (customerId: string) => {
    // Trigger refresh after pricing update
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleGenerateDocument = (customer: Customer) => {
    onGenerateDocument(customer);
  };

  const handleCopyCustomerCode = (customerCode: string) => {
    navigator.clipboard.writeText(customerCode);
    toast.success(`Ügyfélazonosító másolva: ${customerCode}`);
  };

  const handleShowPods = (customer: Customer) => {
    setSelectedCustomerForPods(customer);
    setIsPodListModalOpen(true);
  };

  return (
    <>
      {/* Desktop nézet */}
      <div className="hidden lg:block overflow-hidden border border-gray-200 rounded-lg bg-gray-50">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 bg-white">
            <thead className="bg-gray-50">
              <tr>
                {isInSelectMode && (
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 w-12">
                    <input
                      type="checkbox"
                      checked={selectedCustomers.length === customers.length && customers.length > 0}
                      onChange={onSelectAll}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </th>
                )}
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">
                  {t('customers.fields.name')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">
                  {t('customers.fields.contact')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">
                  {t('customers.fields.organizations')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600">
                  {t('customers.fields.status')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600">
                  {t('customers.fields.notes')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 w-20">
                  {t('customers.fields.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customers.map(customer => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  {isInSelectMode && (
                    <td className="px-2 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={selectedCustomers.some(c => c.id === customer.id)}
                        onChange={() => onSelectCustomer(customer)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </td>
                  )}
                  <td className="px-2 py-1.5">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 rounded bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200 border flex-shrink-0">
                        {customer.type === 'organization' ? (
                          <BuildingOffice2Icon className="h-4 w-4 text-primary-600" />
                        ) : (
                          <UserIcon className="h-4 w-4 text-primary-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Cégnév és badge-ek */}
                        <div className="flex items-center gap-1 flex-wrap">
                          <button
                            onClick={() => onViewClick(customer)}
                            className="text-sm font-medium text-gray-800 leading-tight hover:text-primary-600 hover:underline cursor-pointer transition-colors text-left"
                            title="Részletek megtekintése"
                          >
                            {customer.type === 'organization'
                              ? customer.companyName
                              : `${customer.firstName} ${customer.lastName}`}
                          </button>
                          {customer.dataSource === 'csv_import' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-800 border border-orange-200">
                              📊 CSV
                            </span>
                          )}
                          {customer.dataSource === 'csv_import_2026' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800 border border-purple-200">
                              💰 2026
                            </span>
                          )}
                          {customer.needsReview && (
                            <Tooltip content="Ez az ügyfél felülvizsgálatot igényel">
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                ⚠️
                              </span>
                            </Tooltip>
                          )}
                          {customer.isGasCustomer && (
                            <Tooltip content={t('gas.customer.isGasCustomer')}>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-800 border border-orange-200">
                                🔥 {t('gas.customer.badge')}
                              </span>
                            </Tooltip>
                          )}
                        </div>
                        
                        {/* Szakreferens */}
                        {customer.consultant && (
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-xs text-gray-500">Szakreferens:</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onConsultantClick?.(customer.consultant!.id);
                              }}
                              className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                              title="Szakreferens részletei"
                            >
                              {customer.consultant.companyName}
                            </button>
                          </div>
                        )}
                        
                        {/* Kompakt info sor: ügyfélkód, POD, ár */}
                        <div className="text-[11px] text-gray-600 mt-0.5 leading-tight">
                          {customer.customerCode && (
                            <span className="inline-flex items-center gap-1">
                              <span className="text-primary-600 font-medium">#{customer.customerCode}</span>
                              <button
                                onClick={() => handleCopyCustomerCode(customer.customerCode!)}
                                className="text-gray-400 hover:text-primary-600 transition-colors"
                                title="Ügyfélazonosító másolása"
                              >
                                <ClipboardDocumentIcon className="h-3 w-3" />
                              </button>
                            </span>
                          )}
                          {(() => {
                            const podCount = (customer.pod ? 1 : 0) + (customer.additionalPods?.length || 0);
                            return podCount > 0 ? (
                              <span className="ml-2">
                                <button
                                  onClick={() => handleShowPods(customer)}
                                  className="font-medium text-primary-600 hover:text-primary-800 hover:underline cursor-pointer transition-colors"
                                  title="POD-ok megtekintése"
                                >
                                  📍{podCount}
                                </button>
                                {customer.pod?.meteringType && (
                                  <span className="ml-1 text-gray-500">{customer.pod.meteringType}</span>
                                )}
                              </span>
                            ) : null;
                          })()}
                          {(customer.contractPeriod?.price || customer.contractPeriod?.priceEUR) && (
                            <span className="ml-2">
                              {customer.contractPeriod.price ? (
                                <span className="font-semibold text-green-700">
                                  💰{customer.contractPeriod.price} Ft/kWh
                                </span>
                              ) : customer.contractPeriod.priceEUR ? (
                                <span className="font-semibold text-blue-700">
                                  💰{customer.contractPeriod.priceEUR} EUR/MWh
                                </span>
                              ) : null}
                            </span>
                          )}
                          {(customer.contractPeriod?.startDate || customer.contractPeriod?.endDate) && (
                            <span className="ml-2 text-gray-500">
                              📅 {customer.contractPeriod.startDate || '?'} → {customer.contractPeriod.endDate || '?'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex flex-col gap-0.5">
                      {customer.contactPerson?.name && (
                        <div className="text-[11px] font-medium text-gray-700 leading-tight">
                          👤 {customer.contactPerson.name}
                        </div>
                      )}
                      {customer.contactPerson?.email ? (
                        <a href={`mailto:${customer.contactPerson.email}`} className="text-[11px] text-gray-600 hover:text-primary-600 flex items-center gap-1 leading-tight">
                          <EnvelopeIcon className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{customer.contactPerson.email}</span>
                        </a>
                      ) : customer.email ? (
                        <a href={`mailto:${customer.email}`} className="text-[11px] text-gray-600 hover:text-primary-600 flex items-center gap-1 leading-tight">
                          <EnvelopeIcon className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{customer.email}</span>
                        </a>
                      ) : null}
                      {customer.contactPerson?.phone ? (
                        <a href={`tel:${customer.contactPerson.phone}`} className="text-[11px] text-gray-600 hover:text-primary-600 flex items-center gap-1 leading-tight">
                          <PhoneIcon className="h-3 w-3 flex-shrink-0" />
                          {customer.contactPerson.phone}
                        </a>
                      ) : customer.phone ? (
                        <a href={`tel:${customer.phone}`} className="text-[11px] text-gray-600 hover:text-primary-600 flex items-center gap-1 leading-tight">
                          <PhoneIcon className="h-3 w-3 flex-shrink-0" />
                          {customer.phone}
                        </a>
                      ) : null}
                      {!customer.contactPerson?.email && !customer.email && !customer.contactPerson?.phone && !customer.phone && (
                        <span className="text-[11px] text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex flex-wrap gap-1">
                      {customer.organizations?.map(orgId => {
                        const org = organizations.find((o: SimpleOrganization) => o.id === orgId);
                        return org ? (
                          <div
                            key={org.id}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-50 text-primary-700 border border-primary-200"
                          >
                            <BuildingOffice2Icon className="w-2.5 h-2.5 mr-0.5" />
                            {org.name}
                          </div>
                        ) : null;
                      })}
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex justify-center">
                      <Tooltip content={`Kattintson a státusz váltásához`}>
                        <button
                          onClick={() => onStatusChange(customer)}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium transition-all hover:shadow-md ${
                            customer.status === 'lead' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' :
                            customer.status === 'prospect' ? 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200' :
                            customer.status === 'quoted' ? 'bg-purple-100 text-purple-800 hover:bg-purple-200' :
                            customer.status === 'contract' ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' :
                            customer.status === 'active' ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                            customer.status === 'inactive' ? 'bg-gray-100 text-gray-800 hover:bg-gray-200' :
                            'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          <span className="mr-0.5">
                            {customer.status === 'lead' ? '👋' :
                             customer.status === 'prospect' ? '🎯' :
                             customer.status === 'quoted' ? '📋' :
                             customer.status === 'contract' ? '📝' :
                             customer.status === 'active' ? '✅' :
                             customer.status === 'inactive' ? '⏸️' : '❌'}
                          </span>
                          {customer.status === 'lead' ? 'Érdeklődő' :
                           customer.status === 'prospect' ? 'Potenciális' :
                           customer.status === 'quoted' ? 'Ajánlat' :
                           customer.status === 'contract' ? 'Szerződés' :
                           customer.status === 'active' ? 'Aktív' :
                           customer.status === 'inactive' ? 'Inaktív' : 'Elveszett'}
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex justify-center">
                      {customer.notes ? (
                        <Tooltip content={customer.notes}>
                          <InformationCircleIcon
                            className="h-4 w-4 text-gray-600 hover:text-primary-600 cursor-help transition-colors"
                          />
                        </Tooltip>
                      ) : (
                        <InformationCircleIcon
                          className="h-4 w-4 text-gray-400"
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex justify-end space-x-1.5">
                      {customer.documents && customer.documents.length > 0 && (
                        <Tooltip content={t('customers.tooltips.viewDocuments') || 'View Documents'}>
                          <button
                            onClick={() => onDocumentClick(customer)}
                            className="text-gray-600 hover:text-primary-600"
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                      )}

                      <Tooltip content={t('customers.tooltips.view')}>
                        <button
                          onClick={() => onViewClick(customer)}
                          className="text-gray-600 hover:text-primary-600"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>

                      <Tooltip content="Árazás kezelése">
                        <button
                          onClick={() => handlePricingManagement(customer)}
                          className="text-gray-600 hover:text-green-600"
                        >
                          <CurrencyDollarIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>

                      <Tooltip content={t('customers.tooltips.edit')}>
                        <button
                          onClick={() => onEditClick(customer)}
                          className="text-gray-600 hover:text-primary-600"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('customers.tooltips.delete')}>
                        <button
                          onClick={() => onDeleteClick(customer)}
                          className="text-gray-600 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobil nézet - lista/kártya elrendezés */}
      <div className="lg:hidden space-y-2 mb-2 w-full overflow-hidden bg-gray-50 p-3 rounded-lg">
        {customers.map(customer => (
          <div key={customer.id} className="bg-white rounded-lg p-2 sm:p-3 shadow-md border border-gray-200 w-full overflow-hidden">
            <div className="flex justify-between w-full">
              {isInSelectMode && (
                <div className="flex items-center mr-2">
                  <input
                    type="checkbox"
                    checked={selectedCustomers.some(c => c.id === customer.id)}
                    onChange={() => onSelectCustomer(customer)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                </div>
              )}
              <div className="flex items-center mr-2 mb-1.5 sm:mb-2 max-w-[60%]">
                <div className="p-1 sm:p-1.5 rounded bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200 border mr-1 sm:mr-2 flex-shrink-0">
                  {customer.type === 'organization' ? (
                    <BuildingOffice2Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
                  ) : (
                    <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
                  )}
                </div>
                <div className="min-w-0 w-full overflow-hidden">
                  <div className="text-xs sm:text-sm font-medium text-gray-800 truncate">
                    {customer.type === 'organization'
                      ? customer.companyName
                      : `${customer.firstName} ${customer.lastName}`}
                  </div>
                  {customer.consultant && (
                    <div className="text-[10px] text-gray-500 truncate mt-0.5">
                      Szakreferens: <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onConsultantClick?.(customer.consultant!.id);
                        }}
                        className="font-medium text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                        title="Szakreferens részletei"
                      >
                        {customer.consultant.companyName}
                      </button>
                    </div>
                  )}
                  <div className="text-[10px] sm:text-xs text-gray-600 truncate">
                                         {customer.customerCode && (
                       <div className="flex items-center gap-1 mb-1">
                         <span className="text-primary-600 font-medium">#{customer.customerCode}</span>
                         <button
                           onClick={() => handleCopyCustomerCode(customer.customerCode!)}
                           className="text-gray-400 hover:text-primary-600 transition-colors"
                           title="Ügyfélazonosító másolása"
                         >
                           <ClipboardDocumentIcon className="h-3 w-3" />
                         </button>
                       </div>
                     )}
                    {customer.type === 'organization' && customer.taxNumber && customer.taxNumber}
                  </div>
                  {/* Badge-ek mobil nézetben */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {customer.dataSource === 'csv_import' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-800 border border-orange-200">
                        📊 2025 CSV Importált
                      </span>
                    )}
                    {customer.dataSource === 'csv_import_2026' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800 border border-purple-200">
                        💰 2026 Árazás
                      </span>
                    )}
                    {customer.hasFuturePricing && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        🔮 Jövőbeli árazás
                      </span>
                    )}
                    {customer.needsReview && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                        ⚠️ Felülvizsgálat
                      </span>
                    )}
                    {customer.contractPeriod?.priceUnit && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-800 border border-indigo-200">
                        💰 {customer.contractPeriod.priceUnit}
                      </span>
                    )}
                    {customer.contractPeriod?.paymentCurrency && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                        🏦 {customer.contractPeriod.paymentCurrency}
                      </span>
                    )}
                    {customer.contractPeriod?.indexedPrice && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        📊 Indexált
                        {customer.contractPeriod.spread && (
                          <span className="font-semibold">
                            {customer.contractPeriod.spread} {customer.contractPeriod.priceUnit || 'Ft/kWh'}
                          </span>
                        )}
                      </span>
                    )}
                    {customer.isGasCustomer && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-800 border border-orange-200">
                        🔥 {t('gas.customer.badge')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center flex-shrink-0 ml-auto space-x-1 sm:space-x-2">
                <button
                  onClick={() => onStatusChange(customer)}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
                    customer.status === 'lead' ? 'bg-blue-100 text-blue-800' :
                    customer.status === 'prospect' ? 'bg-cyan-100 text-cyan-800' :
                    customer.status === 'quoted' ? 'bg-purple-100 text-purple-800' :
                    customer.status === 'contract' ? 'bg-amber-100 text-amber-800' :
                    customer.status === 'active' ? 'bg-green-100 text-green-800' :
                    customer.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                    'bg-red-100 text-red-800'
                  }`}
                >
                  <span className="mr-0.5">
                    {customer.status === 'lead' ? '👋' :
                     customer.status === 'prospect' ? '🎯' :
                     customer.status === 'quoted' ? '📋' :
                     customer.status === 'contract' ? '📝' :
                     customer.status === 'active' ? '✅' :
                     customer.status === 'inactive' ? '⏸️' : '❌'}
                  </span>
                  <span className="hidden sm:inline">
                    {customer.status === 'lead' ? 'Érdeklődő' :
                     customer.status === 'prospect' ? 'Potenciális' :
                     customer.status === 'quoted' ? 'Ajánlat' :
                     customer.status === 'contract' ? 'Szerződés' :
                     customer.status === 'active' ? 'Aktív' :
                     customer.status === 'inactive' ? 'Inaktív' : 'Elveszett'}
                  </span>
                </button>
                {customer.documents && customer.documents.length > 0 && (
                  <button
                    onClick={() => onDocumentClick(customer)}
                    className="p-1 rounded-full text-gray-600 hover:text-primary-600 hover:bg-primary-50"
                  >
                    <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                )}
                <button
                  onClick={() => handleGenerateDocument(customer)}
                  className="p-1 rounded-full text-gray-600 hover:text-amber-600 hover:bg-amber-50"
                >
                  <DocumentPlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => handlePricingManagement(customer)}
                  className="p-1 rounded-full text-gray-600 hover:text-green-600 hover:bg-green-50"
                >
                  <CurrencyDollarIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => onEditClick(customer)}
                  className="p-1 rounded-full text-gray-600 hover:text-primary-600 hover:bg-primary-50"
                >
                  <PencilSquareIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => onDeleteClick(customer)}
                  className="p-1 rounded-full text-gray-600 hover:text-red-600 hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
            </div>

            <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] sm:text-xs overflow-hidden">
              {customer.email && (
                <a href={`mailto:${customer.email}`} className="flex items-center text-gray-600 hover:text-primary-600 max-w-full truncate">
                  <EnvelopeIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 flex-shrink-0" />
                  <span className="truncate">{customer.email}</span>
                </a>
              )}
              {customer.phone && (
                <a href={`tel:${customer.phone}`} className="flex items-center text-gray-600 hover:text-primary-600 max-w-full truncate">
                  <PhoneIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 flex-shrink-0" />
                  <span className="truncate">{customer.phone}</span>
                </a>
              )}
            </div>

            {customer.organizations && customer.organizations.length > 0 && (
              <div className="mt-1.5 sm:mt-2 overflow-hidden">
                <div className="flex flex-wrap gap-0.5 sm:gap-1 overflow-hidden">
                  {customer.organizations.map(orgId => {
                    const org = organizations.find((o: SimpleOrganization) => o.id === orgId);
                    return org ? (
                      <div
                        key={org.id}
                        className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200 max-w-[100px] overflow-hidden"
                      >
                        <BuildingOffice2Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1 flex-shrink-0" />
                        <span className="truncate">{org.name}</span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {customer.notes && (
              <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-600 bg-gray-100 p-1.5 sm:p-2 rounded overflow-hidden">
                <div className="line-clamp-2">{customer.notes}</div>
              </div>
            )}

            <div className="mt-2 sm:mt-3 flex justify-end space-x-1 sm:space-x-2">
              <button
                onClick={() => onViewClick(customer)}
                className="text-xs sm:text-sm px-2 py-1 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100"
              >
                {t('customers.actions.view')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pricing Management Modal */}
      <PricingManagementModal
        isOpen={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
        customer={selectedCustomerForPricing}
        onUpdate={handlePricingUpdate}
      />

      {/* POD List Modal */}
      <PodListModal
        isOpen={isPodListModalOpen}
        onClose={() => setIsPodListModalOpen(false)}
        customer={selectedCustomerForPods}
      />
    </>
  );
};

export default CustomerList;


